import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { InfiniteScrollCustomEvent, ModalController, IonSearchbar } from '@ionic/angular';
import { AnalyticsService } from 'src/app/services/analytics/analytics.service';
import { ApiErrorService } from 'src/app/services/api-error.service';
import { AirportServicesBookingService } from 'src/app/services/airport-services-booking/airport-services-booking.service';

interface EsimProduct {
  id: string;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-esim-product-search',
  templateUrl: './esim-product-search.component.html',
  styleUrls: ['./esim-product-search.component.scss'],
})
export class EsimProductSearchComponent implements OnInit, OnDestroy {
  @Input() closeFlag: boolean = true;
  @Input() selectedCountry?: string;

  currentHeaderData: any = {
    modalSlotEnd: true,
    marl20: true,
    title: 'esim.product_search.title',
  };

  productList: EsimProduct[] = [];
  filteredProducts: EsimProduct[] = [];
  noData: boolean = false;
  hasError: boolean = false;
  isLoading: boolean = false;
  
  page: number = 0;
  size: number = 20;
  currentCount: number = 0;
  searchTerm: string = '';
  
  private searchSubscription: Subscription;
  private errorSubscription: Subscription;
  private searchSubject = new Subject<string>();
  
  @ViewChild('productSearch', { static: false }) searchbar: IonSearchbar;

  constructor(
    private airportServicesBooking: AirportServicesBookingService,
    private modalController: ModalController,
    private analyticsService: AnalyticsService,
    private apiErrorService: ApiErrorService
  ) {}

  ngOnInit() {
    this.setupDebouncedSearch();
    this.loadProducts();
    
    this.errorSubscription = this.apiErrorService.value.subscribe((val) => {
      this.hasError = val;
    });
  }

  ionViewDidEnter(): void {
    try {
      this.searchbar?.setFocus();
    } catch (e) {
      console.error('Failed to set focus on searchbar', e);
    }
  }

  setupDebouncedSearch() {
    this.searchSubscription = this.searchSubject
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        map((searchTerm: string) => searchTerm.trim()),
        switchMap((searchTerm: string) => {
          this.searchTerm = searchTerm;
          this.productList = [];
          this.noData = false;
          this.page = 0;
          this.isLoading = true;

          return this.airportServicesBooking.searchEsim(
            this.searchTerm || '*',
            this.page,
            this.size
          );
        })
      )
      .subscribe({
        next: (resData: any) => {
          this.isLoading = false;
          const receivedProducts = resData?.results || [];

          console.log('Search Results:', receivedProducts);
          
          if (receivedProducts && receivedProducts.length > 0) {
            this.currentCount = receivedProducts.length;
            this.productList = receivedProducts;
            this.hasError = false;
            this.noData = false;
          } else {
            this.noData = true;
            this.analyticsService.trackResponseError(
              {
                apiName: 'searchProducts',
                message: 'API responded with empty payload',
                statusCode: 200,
              },
              'ESIM Product Search - No Data'
            );
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.hasError = true;
          this.productList = [];
          this.noData = true;
          this.analyticsService.trackResponseError(
            {
              message: err.message,
              statusCode: err.status,
              apiName: 'searchProducts',
            },
            {
              search_term: this.searchTerm,
              page: this.page,
              size: this.size,
            }
          );
        },
      });
  }

  loadProducts() {
    this.isLoading = true;
    this.airportServicesBooking
      .searchEsim(
        this.searchTerm || '*',
        this.page,
        this.size,
        'category:ESIM'
      )
      .subscribe({
        next: (resData: any) => {
          this.isLoading = false;
          const receivedProducts = resData?.results || [];
          
          if (receivedProducts && receivedProducts.length > 0) {
            this.currentCount = receivedProducts.length;
            this.productList = receivedProducts;
            this.hasError = false;
            this.noData = false;
          } else {
            this.noData = true;
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.hasError = true;
          this.productList = [];
          this.noData = true;
          this.analyticsService.trackResponseError(
            {
              message: err.message,
              statusCode: err.status,
              apiName: 'searchProducts',
            },
            {
              search_term: this.searchTerm,
              page: this.page,
              size: this.size,
            }
          );
        },
      });
  }

  filterProducts(event: any) {
    const searchTerm = event.detail.value;
    this.searchSubject.next(searchTerm);
  }

  filterProductsOnEnter() {
    this.searchSubject.next(this.searchTerm || '');
  }

  getNextPage(event: any) {
    this.page++;

    this.airportServicesBooking
      .searchEsim(
        this.searchTerm || '*',
        this.page,
        this.size,
        'category:ESIM'
      )
      .subscribe({
        next: (resData: any) => {
          const receivedProducts = resData?.results || resData?.products || resData?.result || [];

          if (receivedProducts && receivedProducts.length > 0) {
            this.productList = [...this.productList, ...receivedProducts];
            this.currentCount = receivedProducts.length;
          }

          (event as InfiniteScrollCustomEvent).target.complete();

          if (receivedProducts.length < this.size) {
            event.target.disabled = true;
          }
        },
        error: (err) => {
          (event as InfiniteScrollCustomEvent).target.complete();
          this.page--;
          this.analyticsService.trackResponseError(
            {
              message: err.message,
              statusCode: err.status,
              apiName: 'searchEsim',
            },
            {
              search_term: this.searchTerm,
              page: this.page,
              size: this.size,
            }
          );
        },
      });
  }

  selectProduct(product: EsimProduct) {
    this.modalController.dismiss({
      product,
    });
    // NON-FUNNEL intent signal — the canonical item_selected fires once the
    // user actually lands on /booking-review/:productId.
    this.analyticsService.trackUnified('esim_product_selected', {
      flow: 'esim',
      outcome: 'success',
      legacy_event_name: 'eSIM Product Selected',
      component: 'esim-product-search',
      product_id: product.id,
      product_name: product.name,
      country: (product as any).country || (product as any).title || '',
    });
  }

  closeModal() {
    this.modalController.dismiss();
    this.analyticsService.trackUnified('esim_product_search_modal_closed', {
      flow: 'esim',
      outcome: 'success',
      legacy_event_name: 'eSIM Product Search Modal Closed',
      component: 'esim-product-search',
    });
  }

  /** Display code in chip (like airport code) – country code or first 2 chars of name */
  getProductCode(product: EsimProduct): string {
    const country = product?.country;
    if (country && typeof country === 'string' && country.length <= 3) {
      return country.toUpperCase();
    }
    if (product?.iso2) {
      return String(product.iso2).toUpperCase();
    }
    const name = product?.title || product?.name || '';
    return name.length >= 2 ? name.substring(0, 2).toUpperCase() : '—';
  }

  onSearchClear() {
    this.searchTerm = '';
    this.page = 0;
    this.noData = false;
    this.loadProducts();
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
  }
}
